/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { EndpointsModel, TierModel } from '../../../modules/centralisedManagement/shared/models';
import { Injectable } from '@angular/core';
import { LbRbacService } from '../../../modules/loadbalancer/shared/services/utilities';
import { LogService } from './log.service';
import { MenuConfigModel, MenuItemsModel } from '../../models';
import { PortalRbacService } from '../../../modules/centralisedManagement/shared/services/utils';
import { Subject } from 'rxjs';
import { environment, ModulesEnum } from '../../../../environments/environment';

@Injectable()
export class RbacService {
    /**
     * Content emitter for setting drawer content when navigating between pages
     */
    public requestedUrlEmitter: Subject<any> = new Subject<any>();

    /**
     * Content emitter for setting drawer content when navigating between pages
     */
    public endpointsSetEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Store the routes for all pages.
     * @private
     */
    private _endpointsLookup: EndpointsModel[] = [];

    /**
     * Requested URL.
     * @private
     */
    private _requestedUrl: string;

    /**
     * Store the RBAC formatted menu.
     * @private
     */
    private _menu: MenuConfigModel;

    /**
     * Store the RBAC formatted menu.
     * @private
     */
    private _menus: MenuConfigModel[];

    constructor(
        private lbRbacService: LbRbacService,
        private logService: LogService,
        private portalRbacService: PortalRbacService
    ) {
    }

    set endpointsLookup(endpoints: any) {
        this._endpointsLookup = endpoints;
        this.endpointsSetEmitter.next(true);
    }

    get endpointsLookup() {
        return this._endpointsLookup || [];
    }

    set requestedUrl(url: string) {
        this._requestedUrl = url;
        this.requestedUrlEmitter.next(url);
    }

    get requestedUrl() {
        return this._requestedUrl;
    }

    set menu(menu: MenuConfigModel) {
        this._menu = this.formatMenuPermissions(menu);
    }

    get menu(): MenuConfigModel {
        return this._menu;
    }

    set menus(menus: MenuConfigModel[]) {
        this._menus = this.formatMenus(menus);
    }

    get menus(): MenuConfigModel[] {
        return this._menus;
    }

    /**
     * Page lookup.
     * @param path
     */
    public getPageRequiredRoute(path: string): EndpointsModel {
        const endpoint = this.endpointsLookup.find((item: EndpointsModel): boolean => item.path === path);
        if (!endpoint) {
            this.logService.error('RbacService:getPageRequiredRoute', 'No endpoint found');
        }

        return endpoint;
    }

    /**
     * Get page required routes.
     * @param paths
     */
    public getPageRequiredRoutes(paths: string[]): EndpointsModel[] {
        const endpoints = this.endpointsLookup.filter((item: EndpointsModel) => paths.includes(item.path));
        if (!endpoints || !endpoints.length) {
            this.logService.error('RbacService:getPageRequiredRoutes', 'No endpoints found');
        }

        return endpoints;
    }

    /**
     * Get page permission.
     * @param url
     */
    public getPagePermission(url: string): boolean {
        this.requestedUrl = url;
        const { paths, method } = environment.module === ModulesEnum.CENTRALISED_MANAGEMENT
            ? this.portalRbacService.getPathAndMethodForUrl(url)
            : this.lbRbacService.getPathAndMethodForUrl(url);

        if (paths === '/') {
            return true;
        }

        if (Array.isArray(paths)) {
            const methods: any[] = [];
            for (const path of paths) {
                const endpoint = this.endpointsLookup.find((item: EndpointsModel): boolean => item.path === path);
                methods.push(endpoint?.methods?.[method] || false);
            }

            return methods.every(item => item);
        } else {
            const endpoint = this.endpointsLookup.find((item: EndpointsModel): boolean => item.path === paths);

            return endpoint?.methods?.[method] || false;
        }
    }

    /**
     * Check if user is permitted to access resource from button click.
     * @param reference
     */
    public getPermissionsLookup(reference: any): any {
        const permissionsLookup: object = {};
        for (const property in reference) {
            if (reference.hasOwnProperty(property)) {
                const endpoints = reference[property];
                for (const endpoint of endpoints) {
                    const matchedEndpoints = this.endpointsLookup.filter((item: any): boolean => endpoint.path === item.path);
                    permissionsLookup[property] = !matchedEndpoints.every((item: any) => !this.matchedEndpointHasMethod(endpoint, item));
                    if (!permissionsLookup[property]) {
                        break;
                    }
                }
            }
        }

        return permissionsLookup;
    }

    /**
     * Get look up object.
     */
    public getLookupObject(): any {
        return environment.module === ModulesEnum.CENTRALISED_MANAGEMENT
            ? this.portalRbacService.getLookupObject()
            : this.lbRbacService.getLookupObject();
    }

    /**
     * Check if the matched endpoint has the permission required by the button.
     * @param buttonRef
     * @param matchedEndpoint
     */
    public matchedEndpointHasMethod(buttonRef: any, matchedEndpoint: any): boolean {
        return buttonRef.methods.every((item: any) => matchedEndpoint.methods[item]);
    }

    /**
     * Check the permission are all false.If they are return true to disable the form.
     * @param permission
     */
    public disableForm(permission: any): boolean {
        const values: boolean[] = Object.values(permission) || [];

        return values.every((item: boolean) => !item);
    }

    /**
     * Get part of a menu by id (portal only).
     * @param id
     */
    public getMenuById(id: string): MenuConfigModel {
        const menu: MenuConfigModel[] = this.menus?.filter((item: MenuConfigModel): boolean => item.id === id);
        if (menu?.length > 1) {
            this.logService.error('RbacService:getMenuById', 'More than one menu found');
        } else {
            return menu ? menu[0] : null;
        }
    }

    /**
     * Format menus array
     * @param menus
     */
    public formatMenus(menus: MenuConfigModel[]): MenuConfigModel[] {
        const formattedMenus: MenuConfigModel[] = [];
        for (const menu of menus) {
            formattedMenus.push(this.formatMenuPermissions(menu));
        }

        return formattedMenus;
    }

    /**
     * Set menu permissions (endurance)
     * @param menu
     */
    public formatMenuPermissions(menu: MenuConfigModel): MenuConfigModel {
        if (menu?.menuItems) {
            const lookup: any = this.getLookupObject();

            const subscriptionLookup: any = environment.module === ModulesEnum.CENTRALISED_MANAGEMENT
                ? this.portalRbacService.selectedTier
                : {};

            this.setPermissions(menu.menuItems, lookup, subscriptionLookup);
        }

        return menu;
    }

    /**
     * Set permissions
     * @param menuItems
     * @param lookup
     * @param subscriptionLookup (for logging only)
     * @private
     */
    private setPermissions(menuItems: MenuItemsModel[], lookup: any, subscriptionLookup: TierModel): void {
        for (const item of menuItems) {
            if (item.location) {
                const location: string = item.location.replace('./', '/lb/');
                const paths: any = lookup[location];
                item.hidden = paths ? this.getPermissionByPaths(item, paths, location) : this.getHiddenStateNoPath(item);
                if (Object.keys(subscriptionLookup).length && item.subscriptionPath) {
                    item.subscriptionRequired = this.getSubscriptionState(subscriptionLookup, item.subscriptionPath);
                }
            }

            // Recursive function to target all children.
            if (item.children?.length) {
                this.setPermissions(item.children, lookup, subscriptionLookup);
            }

            // If the item has children, and they are all hidden, hide the parent.
            if (item.children?.length) {
                const childrenHiddenState: boolean[] = item.children.map((item: MenuItemsModel) => item.hidden);
                item.hidden = childrenHiddenState.every((item: any) => item);
            }
        }
    }

    /**
     * Get permission by path from the end-points lookup.
     * @param item
     * @param paths
     * @param location (for logging only)
     * @private
     */
    private getPermissionByPaths(item: MenuItemsModel, paths: any, location: string): boolean {
        if (paths.path === '/' && !paths.method && !item.children) {
            return false;
        }

        if (!paths.method || !paths.path) {
            return true;
        }

        if (item.hiddenForced) {
            return true;
        }

        const permissions: boolean[] = [];

        if (Array.isArray(paths.path)) {
            for (const path of paths.path) {
                permissions.push(this.getPermission(path, paths.method, location));
            }
        } else {
            permissions.push(this.getPermission(paths.path, paths.method, location));
        }

        return paths.type === 'OR' ? !permissions.some((item: any) => item) : !permissions.every((item: any) => item);
    }

    /**
     * Get permissions.
     * @param path
     * @param method
     * @param location (for logging only)
     * @private
     */
    private getPermission(path: any, method: string, location: string) {
        const matchedPath = this.endpointsLookup.find((item: { path: any; }): boolean => item.path === path);
        if (!matchedPath) {
            this.logService.warn(
                'RbacService:getPermission',
                `No match found with path: ${path} and location:  ${location}`);
        }

        return matchedPath?.methods[method] ? matchedPath.methods[method] : false;
    }

    /**
     * Get hidden state if there is no path.
     * @param item
     * @private
     */
    private getHiddenStateNoPath(item: any): boolean {
        // If the hidden attribute is not set, set it to true but false is valid so allow to not hide it
        return item.hiddenForced ? item.hiddenForced : item.hidden === false ? false : true;
    }

    /**
     * Get subscription state
     * @param subscriptionLookup
     * @param subscriptionPath
     * @private
     */
    private getSubscriptionState(subscriptionLookup: any, subscriptionPath: string): boolean {
        if (subscriptionPath) {
            return !subscriptionLookup[subscriptionPath];
        }
    }
}
